import { FC, Fragment, useContext, useState } from 'react';
import { BookingModel } from 'ts/dynamic/interfaces/DynamicBookResponse';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faCalendar,
  faCar,
  faCartFlatbedSuitcase,
  faMoneyBill,
  faPersonWalkingLuggage,
  faPlane,
  faSuitcaseRolling,
  faSuitcase,
  faTicket,
  faUtensils,
  faPerson,
} from '@fortawesome/free-solid-svg-icons';
import { formatPriceCurrency, getCurrency, getLogoUrl } from 'utils/utils';
import { dateFormat } from 'utils/dynamic/i18n';
import { getDescText, getElapsedStr, getStopsText } from 'utils/dynamic/dynamic';
import Rating from 'components/ui/Rating/Rating';
import { PassengerType } from 'constants/dynamic';
import RulesModal from './RulesModel';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import { PickUpDetails } from 'ts/dynamic/interfaces/DynamicTransferUpsell';
import TicketBaggagePopup from '../Details/Flight/TicketBaggagePopup';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import HandleFee from 'components/shared/HandleFee';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import SalesPrice from 'components/shared/SalesPrice';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { MainContext } from 'contexts/MainContext';
import { blobUrl } from 'constants/generic';

interface DynamicBookSummaryProps {
  model: BookingModel;
  couponAmount: string;
  setCouponAmount: (code: string) => void;
  isSuccess?: boolean;
  onClickApply: () => void;
  addPrice?: number;
  bags: any;
}

const DynamicBookSummary: FC<DynamicBookSummaryProps> = ({ model, ...props }) => {
  const { t } = useTranslation();
  const [showRules, setShowRules] = useState(false);
  const { proposal, setProposal } = useContext(MainContext);
  const [showDetailsIndex, setShowDetailsIndex] = useState(-1);
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const isTicketOnly = !!model.eventInfo && !model.flightInfo && !model.hotelInfo;
  const isFMHM = !model.eventInfo && !!model.flightInfo && !!model.hotelInfo;
  const isFMHMSM = !!model.eventInfo && !!model.flightInfo && !!model.hotelInfo;
  const ticketCurrency = sessionStorage.getItem('searchAttractionCurrency') || 'usd';
  const [urlParams] = useSearchParams();
  const searchToken = urlParams.get('searchToken') || '';
  const ticketPaxCount = urlParams.get('ticketPaxCount') || '1';

  const isRedHandbag = (bound: any) =>
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag === 0;
  const isRedTrolley = (bound: any) =>
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley === 0;
  const isGreenTotal = (bound: any) =>
    bound.listOfFlyBaggagePrice &&
    (bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight > 0 ||
      bound.listOfFlyBaggagePrice?.[0]?.pieces > 0);

  const getLocation = (details: PickUpDetails) => {
    if (details.airport) {
      return t('flight.airport') + ' ' + details.airport.code;
    } else if (details.city) {
      return details.country.code + ', ' + (details.city.desc || details.city.code);
    }
  };

  const getPeopleString = () => {
    const adults = model.paxes.filter((pax) => pax.paxTypeCode === PassengerType.Adult).length;
    const children = model.paxes.filter((pax) => pax.paxTypeCode === PassengerType.Child).length;
    const infants = model.paxes.filter((pax) => pax.paxTypeCode === PassengerType.Infant).length;
    const seniors = model.paxes.filter((pax) => pax.paxTypeCode === PassengerType.Senior).length;

    return [
      `${adults} ${t('search.adults')}`,
      `${children} ${t('search.children')}`,
      `${infants} ${t('search.infants')}`,
      `${seniors} ${t('search.seniors')}`,
    ]
      .filter((str) => str.split(' ')[0] !== '0')
      .join(', ');
  };

  const onClickProposal = () => {
    if (!proposal || !setProposal) return;
    const existingProposalIndex = proposal.dynamic.findIndex(
      (item: { searchToken?: string }) => item.searchToken === searchToken
    );
    if (existingProposalIndex !== -1) {
      const updatedDynamic = proposal.dynamic.filter((_, index) => index !== existingProposalIndex);
      setProposal({ ...proposal, dynamic: updatedDynamic });
    } else {
      const bagsPrice =
        props.bags?.reduce(
          (acc: number, bag: any) => acc + bag.displayPrice[model.currency?.toLowerCase() || 'usd'],
          0
        ) || 0;
      let proposalDynamic = {
        hotel: model.hotelInfo?.hotel as any,
        flight: model.flightInfo?.flight as any,
        searchToken: searchToken,
        price: model.totalPrice + bagsPrice,
        currency: model.currency,
        attraction: model.eventInfo?.attraction as any,
        displayPrice: true,
        bags: props.bags,
      };

      if (model.eventInfo && !model.hotelInfo) {
        const currency = sessionStorage.getItem('searchAttractionCurrency') || 'usd';
        proposalDynamic.currency = currency;
        proposalDynamic.price = (model.eventInfo?.attraction?.price?.gross as any)[currency] || 0;
      }

      setProposal({ ...proposal, dynamic: [...proposal.dynamic, proposalDynamic] });
    }
  };

  return (
    <div>
      <div className="details-summary book-summary default-shadow position-relative">
        <ProposalHeart
          onClickPropsal={onClickProposal}
          active={!!proposal?.dynamic.some((item: { searchToken?: string }) => item.searchToken === searchToken)}
        />
        <div className="title-underline title-header-grey text-lg ">
          {t('flight.order_details')}
        </div>
        {!!model.eventInfo && (
          <Fragment>
            <div className="mb-3 text-bold text-contrast">
              {model.eventInfo.attraction.showName}
            </div>
            <div className="mb-10 text-bold text-sm">{model.eventInfo.attraction.city}</div>
          </Fragment>
        )}
        {!!model.hotelInfo && (
          <div className="mb-5 text-sm">
            <FontAwesomeIcon icon={faCalendar} className="mr-5" />
            {model.hotelInfo.hotel.roomClasses[0].nights + 1} {t('hotel.nights')}
          </div>
        )}
        {!!model.paxes.length && !isTicketOnly && (
          <div className="mb-5 text-sm">
            <FontAwesomeIcon icon={faPerson} className="mr-5 text-sm" />
            {model.paxes.length} {t('dyn.people')}
          </div>
        )}
        <div className="mb-20 text-sm">
          <FontAwesomeIcon icon={faSuitcase} className="mr-5" />
          {getDescText(model)}
        </div>
        {!!model.flightInfo && (
          <Fragment>
            <div className="divider mb-20 mt-20"></div>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faPlane} className="text-sm mr-5" />
              {t('flight.details')}{' '}
              {model.flightInfo.flight.flightSource?.includes('ALP') && (
                <span className="text-light">({model.flightInfo.flight.provider})</span>
              )}
            </div>
            {[model.flightInfo.flight.outbound, model.flightInfo.flight.inbound].map(
              (bound, index) => (
                <div key={index}>
                  <div
                    className="text-lg mt-5 mb-5 position-relative flex-end"
                    onMouseEnter={() => setShowDetailsIndex(index)}
                    onMouseLeave={() => setShowDetailsIndex(-1)}
                  >
                    {showDetailsIndex === index && <TicketBaggagePopup outbond={bound as any} />}
                    {bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag !== -1 && (
                      <span className="mr-10">
                        <FontAwesomeIcon
                          icon={faSuitcaseRolling}
                          className={isRedHandbag(bound) ? 'color-danger' : 'text-success'}
                        />
                      </span>
                    )}

                    {bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley !== -1 && (
                      <span className="mr-10">
                        <FontAwesomeIcon
                          icon={faPersonWalkingLuggage}
                          className={isRedTrolley(bound) ? 'color-danger' : 'text-success'}
                        />
                      </span>
                    )}
                    {bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight !== -1 && (
                      <span className="mr-10">
                        <FontAwesomeIcon
                          icon={faCartFlatbedSuitcase}
                          className={isGreenTotal(bound) ? 'text-success' : 'color-danger'}
                        />
                      </span>
                    )}
                  </div>
                  <div className="dyn-ticket text-xsm" key={index}>
                    <div className="dyn-ticket-logo text-bold">
                      <span>
                        <FontAwesomeIcon icon={faPlane} className="mr-5" />
                        {t(index === 0 ? 'dyn.outbound_flight' : 'dyn.return_flight')}
                      </span>
                      <img src={getLogoUrl(blobUrl, bound.airline.code)} alt={bound.airline.desc} />
                    </div>
                    <div className="dyn-ticket-time">
                      <div>{bound.departureAirport.cityDesc.slice(0, 10)}</div>
                      <div className="text-bold">
                        {dateFormat(bound.departureDateTime, 'dd.MM')}
                      </div>
                      <div className="text-bold text-st">
                        {dateFormat(bound.departureDateTime, 'HH:mm')}
                      </div>
                    </div>
                    <div className="dyn-ticket-stops">
                      <div className="dyn-ticket-stops-duration">
                        {getElapsedStr(bound.elapsedDTime)}
                        {bound.gdsFlyAirs.length > 1 && (
                          <span className="dyn-ticket-stops-point"></span>
                        )}
                      </div>
                      <div className="dyn-ticket-stops-airports">
                        <span className="text-bold">{bound.departureAirport.code}</span>
                        <span>{getStopsText(bound)}</span>
                        <span className="text-bold">{bound.arrivalAirport.code}</span>
                      </div>
                    </div>
                    <div className="dyn-ticket-time">
                      <div>{bound.arrivalAirport.cityDesc.slice(0, 10)}</div>
                      <div className="text-bold">{dateFormat(bound.arrivalDateTime, 'dd.MM')}</div>
                      <div className="text-bold text-st">
                        {dateFormat(bound.arrivalDateTime, 'HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </Fragment>
        )}
        {!!model.hotelInfo && (
          <Fragment>
            <div className="divider mb-20 mt-20"></div>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faBed} className="text-sm mr-5" />
              {t('hotel.details')}
            </div>
            <div className="text-sm text-lh">
              <div className="text-st text-bold">{model.hotelInfo.hotel.item.name}</div>
              <Rating value={model.hotelInfo.hotel.item.star} />
              <div className="text-bold">
                {model.hotelInfo.roomClass.desc ||
                  (model.hotelInfo.roomClass.hotelRooms.length &&
                    model.hotelInfo.roomClass.hotelRooms[0].desc) +
                    ' | ' +
                    model.hotelInfo.roomClass.board.basis.desc}
              </div>
              ({getPeopleString()})
              {model.hotelInfo.roomClass.board.basis.code !== 'RO' && (
                <FontAwesomeIcon icon={faUtensils} className="ph-10" />
              )}
              <div className="display-flex flex-wrap">
                <div className="w50p">
                  <div className="text-light">{t('search.check_in')}</div>
                  <div className="text-bold">
                    {dateFormat(model.hotelInfo.roomClass.checkIn, 'EEE - dd MMM, yyyy')}
                  </div>
                </div>
                <div className="w50p">
                  <div className="text-light">{t('search.check_out')}</div>
                  <div className="text-bold">
                    {dateFormat(model.hotelInfo.roomClass.checkOut, 'EEE - dd MMM, yyyy')}
                  </div>
                </div>
                <div className="w50p">
                  <div className="text-light">{t('hotel.nights_up')}</div>
                  <div className="text-bold">{model.hotelInfo.roomClass.nights}</div>
                </div>
                <div className="w50p">
                  <div className="text-light">{t('hotel.rooms')}</div>
                  <div className="text-bold">1</div>
                </div>
              </div>
            </div>
          </Fragment>
        )}
        <div className="divider mb-20 mt-20"></div>
        {model.eventInfo && (
          <Fragment>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faTicket} className="text-sm mr-5" />
              {t('dyn.card')}
            </div>
            <div className="text-sm text-lh">
              <div className="text-st text-bold">{model.eventInfo.attraction.showName}</div>
              <div className="">{model.eventInfo.attraction.ticketDesc}</div>
              <div>
                {model.eventInfo.attraction.country}, {model.eventInfo.attraction.city} -{' '}
                {model.eventInfo.attraction.vanueDesc}
              </div>
              <div>
                <span className=" mr-5">{t('dyn.ticket_date')}</span>
                <span className="text-bold">
                  {dateFormat(model.eventInfo.attraction.eventDate, 'dd.MM.yy - HH:mm')}
                </span>
              </div>
              <div>
                <span className=" mr-5">{t('dyn.tickets_amount')}</span>
                <span className="text-bold">{ticketPaxCount}</span>
              </div>
            </div>
            <div className="divider mb-20 mt-20"></div>
          </Fragment>
        )}
        {model.transferInfo &&
          (!!model.transferInfo.transfer.selectedForward ||
            !!model.transferInfo.transfer.selectedReturn) && (
            <Fragment>
              <div className="title text-st text-contrast">
                <FontAwesomeIcon icon={faCar} className="text-sm mr-5" />
                {t('transfer.details')}
              </div>
              {!!model.transferInfo.transfer.selectedForward && (
                <div className="text-lh text-sm">
                  <div className="text-st text-bold">
                    {model.transferInfo.transfer.selectedForward.transfer.transferItem.desc}
                  </div>
                  <div>
                    <span>{t('flight.date')}: </span>
                    <span className="text-bold">
                      {dateFormat(
                        model.transferInfo.transfer.selectedForward.transfer.pickUpDate,
                        'dd MMM yyyy HH:mm'
                      )}
                    </span>
                  </div>
                  <div>
                    <span>{t('transfer.from')}: </span>
                    <span className="text-bold">
                      {getLocation(
                        model.transferInfo.transfer.selectedForward.transfer.pickUpDetails
                      )}
                    </span>
                  </div>
                  <div>
                    <span>{t('transfer.to')}: </span>
                    <span className="text-bold">
                      {getLocation(
                        model.transferInfo.transfer.selectedForward.transfer.dropOffDetails
                      )}
                    </span>
                  </div>
                </div>
              )}
              {!!model.transferInfo.transfer.selectedReturn && (
                <div className="text-lh text-sm mt-20">
                  <div className="text-st text-bold">
                    {model.transferInfo.transfer.selectedReturn.transfer.transferItem.desc}
                  </div>
                  <div>
                    <span>{t('flight.date')}: </span>
                    <span className="text-bold">
                      {dateFormat(
                        model.transferInfo.transfer.selectedReturn.transfer.pickUpDate,
                        'dd MMM yyyy HH:mm'
                      )}
                    </span>
                  </div>
                  <div>
                    <span>{t('transfer.from')}: </span>
                    <span className="text-bold">
                      {getLocation(
                        model.transferInfo.transfer.selectedReturn.transfer.pickUpDetails
                      )}
                    </span>
                  </div>
                  <div>
                    <span>{t('transfer.to')}: </span>
                    <span className="text-bold">
                      {getLocation(
                        model.transferInfo.transfer.selectedReturn.transfer.dropOffDetails
                      )}
                    </span>
                  </div>
                </div>
              )}
              <div className="divider mb-20 mt-20"></div>
            </Fragment>
          )}
        <div className="title text-st text-contrast">
          <FontAwesomeIcon icon={faFile} className="text-sm mr-5" />
          {t('dyn.general_conditions')}
        </div>
        <div className="text-sm text-lh">
          <div className="text-bold">{t('dyn.its_your_read')}:</div>{' '}
          <div className="text-contrast cur-poi" onClick={() => setIsConditionsOpen(true)}>
            {t('dyn.terms')}
          </div>
          <div className="text-contrast cur-poi" onClick={() => setIsConditionsOpen(true)}>
            {t('dyn.general_conditions_cancellation')}
          </div>
          <div className="text-bold mb-10">{t('dyn.all_conditions_time')}</div>
          {!!model.flightInfo && (
            <div className="mb-10">
              <div className="text-bold">{t('flight.s')}:</div>
              <div>
                <span>{t('flight.it_is_your')}</span>
                <span
                  className="text-contrast text-bold cur-poi text-underline"
                  onClick={() => setShowRules(true)}
                >
                  {t('flight.all_rules')}
                </span>
                <span>{t('flight.according_this')}</span>
              </div>
            </div>
          )}
          {model.eventInfo && (
            <div className="mb-10">
              <div className="text-bold">{t('dyn.ticket')}:</div>
              <div>
                <span>{t('dyn.the_event_ticket')} </span>
                <span className="text-contrast text-bold cur-poi">
                  {t('dyn.full_cancellation_fees')}{' '}
                </span>
                <span>{t('dyn.from_the_moment')}</span>
              </div>
            </div>
          )}
          {!!model.hotelInfo?.cancellationPolicy && (
            <div className="mb-10">
              <div className="text-bold">{t('hotel.hotel')}:</div>
              <div>
                {t('dyn.no_cancellation_fees')}:{' '}
                <span className="text-bold">
                  {dateFormat(model.hotelInfo.cancellationPolicy.date, 'EEEE - MMMM dd, yyyy')}
                </span>
              </div>
            </div>
          )}
          {isFMHM && <HandleFee name="dynamic_fmhm" />}
          {isFMHMSM && <HandleFee name="dynamic_fmhmsm" />}
        </div>
        <div className="divider mb-20 mt-20"></div>
        <div className="flex-between title-footer-grey">
          <div className="text-bold text-contrast">
            <FontAwesomeIcon icon={faMoneyBill} className="text-sm mr-5" />
            {t('flight.order_summary')}
          </div>
          {!props.isSuccess && (
            <div>
              {isTicketOnly && (
                <div className="text-bold text-contrast text-md text-right mb-5">
                  {formatPriceCurrency(
                    (model.eventInfo.attraction.price.gross as any)[ticketCurrency] *
                      +ticketPaxCount,
                    getCurrency(ticketCurrency)
                  )}
                </div>
              )}
              {!isTicketOnly && (
                <Fragment>
                  <div className="text-bold text-contrast text-md text-right mb-5">
                    {formatPriceCurrency(
                      model.totalPrice + (props.addPrice || 0) + +(props.couponAmount || '0'),
                      model.currencySymbol
                    )}
                  </div>
                  {!!model.paxes.length && (
                    <div className="text-xsm text-light">
                      {t('flight.total_average_passenger')}:{' '}
                      {formatPriceCurrency(
                        (model.totalPrice + (props.addPrice || 0) + +(props.couponAmount || '0')) /
                          model.paxes.length,
                        model.currencySymbol
                      )}
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
          {props.isSuccess && (
            <div>
              <div className="text-bold text-contrast text-md text-right mb-5">
                {formatPriceCurrency(model.totalPrice, model.currencySymbol)}
              </div>
              <div className="text-xsm text-light">
                {t('flight.total_average_passenger')}:{' '}
                {formatPriceCurrency(model.totalPrice / model.paxes.length, model.currencySymbol)}
              </div>
            </div>
          )}
        </div>
        {showRules && !!model.flightInfo && (
          <RulesModal
            flightId={model.flightInfo.flight.id}
            onClose={() => setShowRules(false)}
            tokenKey={'flightToken'}
          />
        )}
        {isConditionsOpen && (
          <GeneralConditionsModal product="Dynamic" onClose={() => setIsConditionsOpen(false)} />
        )}
      </div>
      {!props.isSuccess && (
        
          <SalesPrice
            currency={isTicketOnly ? ticketCurrency : model.currency}
            setSaleValue={props.setCouponAmount}
            saleValue={props.couponAmount}
            price={
              isTicketOnly
                ? (model.eventInfo.attraction.price.gross as any)[ticketCurrency] * +ticketPaxCount
                : model.totalPrice + (props.addPrice || 0)
            }
            onClickApply={props.onClickApply}
          />
        
      )}
    </div>
  );
};

export default withErrorBoundary(DynamicBookSummary, 'DynamicBookSummary');
